import {
  SettingHeader,
  SettingsContainer,
  SettingsContent
} from '@components/ui/settings';
import useProcess from '@hooks/useProcess';
import useStores from '@hooks/useStore';
import { useWorkflow } from '@hooks/useWorkflow';
import { Loader2 } from 'lucide-react';
import { observer } from 'mobx-react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { LoaderBox } from '@/components/ui/loader';
import { ParamsList } from '@/routes/routes.types';

const WorkflowAdminSetting = () => {
  const process = useProcess();
  const { workflow } = useWorkflow();
  const { workflowStore } = useStores();
  const navigate = useNavigate();
  const processId = useParams()[ParamsList.ProcessId] as string;

  const [publishedId, setPublishedId] = useState<string>(
    workflow?.published_id ?? ''
  );
  const [idEnvVarName, setIdEnvVarName] = useState<string>(
    workflow?.id_environment_variable_name ?? ''
  );
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [workflowName, setWorkflowName] = useState<string>(
    workflow?.name ?? ''
  );
  const [emptyNameError, setEmptyNameError] = useState(false);

  useEffect(() => {
    setPublishedId(workflow?.published_id ?? '');
  }, [workflow?.published_id]);

  useEffect(() => {
    setIdEnvVarName(workflow?.id_environment_variable_name ?? '');
  }, [workflow?.id_environment_variable_name]);

  useEffect(() => {
    setWorkflowName(workflow?.name ?? '');
  }, [workflow?.name]);

  if (!process) {
    return (
      <LoaderBox>
        <Loader2 className="size-6 animate-spin text-muted-foreground" />
      </LoaderBox>
    );
  }

  if (!workflow) {
    return (
      <LoaderBox>
        <Loader2 className="size-6 animate-spin text-muted-foreground" />
      </LoaderBox>
    );
  }

  const onNameBlur = () => {
    if (!workflowName.trim()) {
      setEmptyNameError(true);
      return;
    }
    if (emptyNameError) {
      setEmptyNameError(false);
    }
    workflow.name = workflowName.trim();
  };

  const onPublishedIdBlur = async () => {
    const newValue = publishedId.trim() || null;
    if (newValue === workflow.published_id) {
      return;
    }
    await workflowStore.adminUpdateWorkflow(workflow.id, {
      published_id: newValue
    });
  };

  const onIdEnvVarNameBlur = async () => {
    if (idEnvVarName === workflow.id_environment_variable_name) {
      return;
    }
    await workflowStore.adminUpdateWorkflow(workflow.id, {
      id_environment_variable_name: idEnvVarName
    });
  };

  const onDeleteWorkflow = async () => {
    setDeleting(true);
    const success = await workflowStore.adminDeleteWorkflow(workflow.id);
    setDeleting(false);
    if (success) {
      navigate(`/${processId}/general`);
    }
  };

  return (
    <SettingsContainer>
      <SettingHeader
        description="Workflow configuration reserved for superusers and developers"
        title="Workflow Admin Settings"
      />
      <SettingsContent className="gap-6 self-start pb-8">
        <div className="rounded-xl bg-card p-6 shadow-verail">
          <h3 className="mb-4 font-medium text-lg">Workflow Settings</h3>
          <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-2">
              <Label htmlFor="workflow-name">Name</Label>
              <Input
                id="workflow-name"
                onBlur={() => onNameBlur()}
                onChange={e => setWorkflowName(e.target.value)}
                placeholder="Renewal"
                value={workflowName}
              />
              {emptyNameError && (
                <p className="text-destructive text-sm">
                  Workflow name can't be empty
                </p>
              )}
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="published-id">Published ID</Label>
              <Input
                id="published-id"
                onBlur={() => void onPublishedIdBlur()}
                onChange={e => setPublishedId(e.target.value)}
                placeholder="Not published yet"
                value={publishedId}
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="id-env-var-name">
                ID Environment Variable Name
              </Label>
              <Input
                id="id-env-var-name"
                onBlur={() => void onIdEnvVarNameBlur()}
                onChange={e => setIdEnvVarName(e.target.value)}
                placeholder="WORKFLOW_ID"
                value={idEnvVarName}
              />
            </div>
          </div>
        </div>

        <div className="rounded-xl bg-card p-6 shadow-verail">
          <h3 className="mb-1 font-medium text-lg">Danger Zone</h3>
          <p className="mb-4 text-muted-foreground text-sm">
            Permanently delete this workflow and all its data
          </p>
          <div className="flex items-center justify-between">
            <p className="text-sm">
              Once you delete a workflow, there is no going back.
            </p>
            <Dialog onOpenChange={setDeleteDialogOpen} open={deleteDialogOpen}>
              <Button
                onClick={() => setDeleteDialogOpen(true)}
                size="sm"
                type="button"
                variant="destructive"
              >
                Delete this workflow
              </Button>
              <DialogContent>
                <DialogHeader className="space-y-4">
                  <DialogTitle>Delete workflow</DialogTitle>
                  <DialogDescription>
                    This will permanently delete the workflow and all its
                    actions, events, gateways, and associated data. This action
                    cannot be undone.
                  </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                  <Button
                    onClick={() => setDeleteDialogOpen(false)}
                    type="button"
                    variant="outline"
                  >
                    Cancel
                  </Button>
                  <Button
                    disabled={deleting}
                    onClick={() => void onDeleteWorkflow()}
                    type="button"
                    variant="destructive"
                  >
                    {deleting && (
                      <Loader2 className="mr-2 size-4 animate-spin" />
                    )}
                    Delete
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </SettingsContent>
    </SettingsContainer>
  );
};

export default observer(WorkflowAdminSetting);
